import { Button } from '@/components/ui/button'
import { getErrorMessage } from '@/lib/error'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import SecondaryPageLayout from '@/layouts/SecondaryPageLayout'
import { HallwayUniverse } from '@/types/universe'
import {
  DeployNsiteParams,
  NsiteDeployStage,
  deployNsite,
  gatewayUrlsFor
} from '@/services/nsite-deploy.service'
import { accountAtom } from '@/atoms/session'
import { HugeiconsIcon } from '@hugeicons/react'
import {
  AlertCircleIcon,
  ArrowRight01Icon,
  CheckmarkCircle02Icon,
  Loading03Icon,
  SourceCodeIcon
} from '@hugeicons/core-free-icons'
import { useAtomValue } from 'jotai'
import { createContext, forwardRef, useContext, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { isValidDTag } from '@/lib/base36'
import { isValidPubkey } from '@/lib/pubkey'
import { isWebsocketUrl, normalizeUrl } from '@/lib/url'
import { decodeNostrURI } from '@nostr/tools/nip19'
import { isNip05, queryProfile } from '@nostr/tools/nip05'

// Mirror of deployer/stringFields: simple string values.
const SIMPLE_STRING_FIELDS: (keyof HallwayUniverse)[] = [
  'appName',
  'baseShareURL',
  'homeMarkdown',
  'defaultPrimaryColor',
  'defaultThemeSetting',
  'notificationListStyleDefault',
  'noteListStyleDefault',
  'imgproxyUrl',
  'imgproxyKey',
  'imgproxySalt',
  'pomegranateCentral',
  'translationsURL',
  'registryOfKindsURL',
  'linkPreviewService',
  'genericCorsProxy',
  'avatarStyleDefault'
]

// Mirror of deployer/stringListFields: one URL/string per line.
const LIST_FIELDS: (keyof HallwayUniverse)[] = [
  'defaultFavoriteRelays',
  'defaultBlossomServers',
  'defaultNostrConnectRelays',
  'bigRelayUrls',
  'indexerUrls',
  'defaultInbox',
  'defaultOutbox',
  'noteSearchRelayUrls',
  'noteSearchRelayUrlsExtra',
  'profileSearchRelayUrls',
  'readRepliesFrom',
  'publishTargets',
  'pomegranateOperators'
]

// The subset of LIST_FIELDS that holds relay URLs (validated as websocket URLs).
const RELAY_LIST_FIELDS: (keyof HallwayUniverse)[] = LIST_FIELDS.filter(
  (f) => f !== 'defaultBlossomServers' && f !== 'pomegranateOperators'
)

// Relay lists shown together in the "Relays" section; the rest have their own sections.
const RELAYS_SECTION_FIELDS: (keyof HallwayUniverse)[] = RELAY_LIST_FIELDS.filter(
  (f) => f !== 'readRepliesFrom' && f !== 'publishTargets'
).concat('defaultBlossomServers')

// Mirror of deployer/boolFields.
const BOOL_FIELDS: (keyof HallwayUniverse)[] = [
  'groupedDefault',
  'includeRepliesDefault',
  'protectedDefault',
  'sidebarCollapseDefault',
  'enableSingleColumnLayoutDefault',
  'readRepliesFromExclusive',
  'publishTargetsExclusive',
  'dearrowYoutube',
  'hideNotificationsDefault',
  'hideConversationsDefault',
  'hideFollowingDefault',
  'hideFavoriteRelaysDefault',
  'enableNipA3'
]

const PRIMARY_COLOR_KEYS = [
  'DEFAULT',
  'RED',
  'ORANGE',
  'AMBER',
  'YELLOW',
  'LIME',
  'GREEN',
  'EMERALD',
  'TEAL',
  'CYAN',
  'SKY',
  'BLUE',
  'INDIGO',
  'VIOLET',
  'PURPLE',
  'FUCHSIA',
  'PINK',
  'ROSE'
] as const

const THEME_KEYS = ['system', 'light', 'dark'] as const
const AVATAR_KEYS = ['circle', 'square'] as const

// Helper: turn an array into "url per line" form state and back.
function listToString(arr: (string | number)[] | undefined): string {
  return (arr ?? []).join('\n')
}
function stringToList(s: string): string[] {
  return s
    .split('\n')
    .map((x) => x.trim())
    .filter(Boolean)
}

function stringToKinds(s: string): number[] {
  return s
    .split('\n')
    .map((x) => parseInt(x.trim(), 10))
    .filter((n) => Number.isInteger(n))
}

function fieldLabel(field: string): string {
  return field
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (c) => c.toUpperCase())
    .trim()
}

// --- list normalization/validation -------------------------------------------

type NormalizeResult = { text: string; invalid: string[] }
type Normalizer = (text: string) => Promise<NormalizeResult>

// Builds a Normalizer that runs `normalizeLine` over every non-empty line,
// replacing each with its normalized form (deduplicated). Lines that can't be
// normalized are kept as typed so the user can fix them, and are reported.
function lineNormalizer(
  normalizeLine: (line: string) => string | null | Promise<string | null>
): Normalizer {
  return async (text) => {
    const lines = stringToList(text)
    const values = await Promise.all(lines.map((line) => normalizeLine(line)))
    const out: string[] = []
    const invalid: string[] = []
    lines.forEach((line, i) => {
      const value = values[i]
      if (value === null) {
        invalid.push(line)
        out.push(line)
      } else if (!out.includes(value)) {
        out.push(value)
      }
    })
    return { text: out.join('\n'), invalid }
  }
}

const normalizeRelays = lineNormalizer((line) => {
  const url = normalizeUrl(line.includes('://') ? line : `wss://${line}`)
  if (!url || !isWebsocketUrl(url)) return null
  const { hostname } = new URL(url)
  if (!hostname.includes('.') && hostname !== 'localhost' && !hostname.startsWith('[')) return null
  return url
})

const normalizePubkeys = lineNormalizer(async (line) => {
  const lower = line.toLowerCase()
  if (isValidPubkey(lower)) return lower

  const decoded = decodeNostrURI(line)
  if (decoded.type === 'npub') return decoded.data
  if (decoded.type === 'nprofile') return decoded.data.pubkey

  if (isNip05(line) || /^[\w-]+(\.[\w-]+)+$/.test(line)) {
    try {
      return (await queryProfile(line))?.pubkey ?? null
    } catch {
      return null
    }
  }
  return null
})

const normalizeKinds = lineNormalizer((line) => {
  if (!/^\d+$/.test(line)) return null
  const n = parseInt(line, 10)
  return n <= 65535 ? String(n) : null
})

// Invalid entries per list field, keyed by field name (top-level lists) or
// `${itemId}:${field}` (lists inside repeatable items).
const ValidationContext = createContext<{
  invalid: Record<string, string[]>
  report: (key: string, invalid: string[]) => void
}>({ invalid: {}, report: () => {} })

// --- form state ----------------------------------------------------------------

// Stable ids for repeatable items, used as React keys and validation keys.
let lastItemId = 0
const newItemId = () => ++lastItemId

// Build a deep-cloned form-state object out of the current universe.
// Lists are kept as raw newline-separated text so the textareas can hold
// blank/partial lines while typing; they're parsed in compactForUniverse.
function makeInitialState(u: HallwayUniverse) {
  return {
    ...u,
    feedKinds: listToString(u.feedKinds),
    hardcodedFeeds:
      u.hardcodedFeeds?.map((f) => ({
        _id: newItemId(),
        name: f.name ?? '',
        icon: f.icon ?? '',
        relays: listToString(f.relays),
        pubkeys: listToString(f.pubkeys),
        kinds: listToString(f.kinds)
      })) ?? [],
    customLinks:
      u.customLinks?.map((l) => ({
        _id: newItemId(),
        label: l.label ?? '',
        hint: l.hint ?? '',
        url: l.url ?? ''
      })) ?? [],
    kindGroups:
      u.kindGroups?.map((g) => ({
        _id: newItemId(),
        kinds: listToString(g.kinds),
        label: g.label ?? '',
        disabled: g.disabled ?? false
      })) ?? [],
    clientHandlersFallback:
      u.clientHandlers?.fallback?.map((h) => ({
        _id: newItemId(),
        name: h.name ?? '',
        urlPattern: h.urlPattern ?? ''
      })) ?? [],
    clientHandlersByKind: Object.entries(u.clientHandlers?.byKind ?? {}).flatMap(([kind, hs]) =>
      hs.map((h) => ({
        _id: newItemId(),
        kind,
        name: h.name ?? '',
        urlPattern: h.urlPattern ?? ''
      }))
    ),
    translations: Object.entries(u.translations ?? {}).map(([key, value]) => ({
      _id: newItemId(),
      key,
      value
    }))
  }
}

type FormState = ReturnType<typeof makeInitialState>

// Keys of FormState that hold arrays of repeatable items.
type RepeatableKey =
  | 'hardcodedFeeds'
  | 'customLinks'
  | 'kindGroups'
  | 'clientHandlersFallback'
  | 'clientHandlersByKind'
  | 'translations'

// Normalizes every validated list in the form. Returns the normalized state and
// lists, every field's invalid entries, and human-readable error lines.
async function normalizeForm(s: FormState, lists: Record<string, string>) {
  const invalid: Record<string, string[]> = {}
  const errors: string[] = []
  async function check(key: string, label: string, text: string, normalize: Normalizer) {
    const res = await normalize(text)
    if (res.invalid.length) {
      invalid[key] = res.invalid
      errors.push(`${label}: ${res.invalid.join(', ')}`)
    }
    return res.text
  }

  const nextLists = { ...lists }
  for (const f of RELAY_LIST_FIELDS) {
    nextLists[f] = await check(f, fieldLabel(f), lists[f] ?? '', normalizeRelays)
  }

  const hardcodedFeeds = []
  for (const [i, f] of s.hardcodedFeeds.entries()) {
    hardcodedFeeds.push({
      ...f,
      relays: await check(`${f._id}:relays`, `Feed ${i + 1} relays`, f.relays, normalizeRelays),
      pubkeys: await check(
        `${f._id}:pubkeys`,
        `Feed ${i + 1} pubkeys`,
        f.pubkeys,
        normalizePubkeys
      ),
      kinds: await check(`${f._id}:kinds`, `Feed ${i + 1} kinds`, f.kinds, normalizeKinds)
    })
  }

  const kindGroups = []
  for (const [i, g] of s.kindGroups.entries()) {
    kindGroups.push({
      ...g,
      kinds: await check(`${g._id}:kinds`, `Kind group ${i + 1} kinds`, g.kinds, normalizeKinds)
    })
  }

  const feedKinds = await check('feedKinds', 'Feed Kinds', s.feedKinds, normalizeKinds)

  for (const [i, h] of s.clientHandlersByKind.entries()) {
    if (h.kind && !/^\d+$/.test(h.kind.trim())) {
      errors.push(`Client handler by kind ${i + 1}: invalid kind "${h.kind}"`)
    }
  }

  return {
    state: { ...s, hardcodedFeeds, kindGroups, feedKinds },
    lists: nextLists,
    invalid,
    errors
  }
}

// Filter out empty fields so the resulting universe stays close to the
// deployer/server contract: no `field: ''` entries for unset strings.
function compactForUniverse(s: FormState, lists: Record<string, string>): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const f of SIMPLE_STRING_FIELDS as string[]) {
    const v = (s as Record<string, unknown>)[f]
    if (typeof v === 'string' && v.trim()) out[f] = v.trim()
  }
  for (const f of LIST_FIELDS as string[]) {
    const arr = stringToList(lists[f] ?? '')
    if (arr.length) out[f] = arr
  }
  for (const f of BOOL_FIELDS as string[]) {
    if ((s as Record<string, unknown>)[f]) out[f] = true
  }
  const feedKinds = stringToKinds(s.feedKinds)
  if (feedKinds.length) out.feedKinds = feedKinds
  if (s.groupedTimeframe) out.groupedTimeframe = s.groupedTimeframe
  if (s.maxEmbedDepth) out.maxEmbedDepth = s.maxEmbedDepth
  if (s.logoUrl?.trim()) out.logoUrl = s.logoUrl.trim()

  const feeds: {
    name?: string
    icon?: string
    relays?: string[]
    pubkeys?: string[]
    kinds?: number[]
  }[] = []
  for (const raw of s.hardcodedFeeds) {
    const feed: (typeof feeds)[number] = {}
    if (raw.name.trim()) feed.name = raw.name.trim()
    if (raw.icon.trim()) feed.icon = raw.icon.trim()
    const relays = stringToList(raw.relays)
    if (relays.length) feed.relays = relays
    const pubkeys = stringToList(raw.pubkeys)
    if (pubkeys.length) feed.pubkeys = pubkeys
    const kinds = stringToKinds(raw.kinds)
    if (kinds.length) feed.kinds = kinds
    if (Object.keys(feed).length) feeds.push(feed)
  }
  if (feeds.length) out.hardcodedFeeds = feeds

  const groups: { kinds: number[]; label?: string; disabled?: boolean }[] = []
  for (const raw of s.kindGroups) {
    const kinds = stringToKinds(raw.kinds)
    if (!kinds.length) continue
    const group: (typeof groups)[number] = { kinds }
    if (raw.label.trim()) group.label = raw.label.trim()
    if (raw.disabled) group.disabled = true
    groups.push(group)
  }
  if (groups.length) out.kindGroups = groups

  const links: { label: string; hint?: string; url: string }[] = []
  for (const raw of s.customLinks) {
    const label = raw.label.trim()
    const url = raw.url.trim()
    if (!label || !url) continue
    const hint = raw.hint.trim()
    links.push(hint ? { label, hint, url } : { label, url })
  }
  if (links.length) out.customLinks = links

  const fallback: { name: string; urlPattern: string }[] = []
  for (const h of s.clientHandlersFallback) {
    const name = h.name.trim()
    const urlPattern = h.urlPattern.trim()
    if (name && urlPattern) fallback.push({ name, urlPattern })
  }
  const byKind: Record<string, { name: string; urlPattern: string }[]> = {}
  for (const h of s.clientHandlersByKind) {
    const kind = parseInt(h.kind.trim(), 10)
    const name = h.name.trim()
    const urlPattern = h.urlPattern.trim()
    if (!Number.isInteger(kind) || !name || !urlPattern) continue
    ;(byKind[kind] ??= []).push({ name, urlPattern })
  }
  if (fallback.length || Object.keys(byKind).length) {
    const ch: { fallback?: typeof fallback; byKind?: typeof byKind } = {}
    if (fallback.length) ch.fallback = fallback
    if (Object.keys(byKind).length) ch.byKind = byKind
    out.clientHandlers = ch
  }

  const translations: Record<string, string> = {}
  for (const t of s.translations) {
    if (t.key.trim()) translations[t.key.trim()] = t.value
  }
  if (Object.keys(translations).length) out.translations = translations

  return out
}

const ForkPage = forwardRef(({ index }: { index?: number }, ref) => {
  const u = window.hallway.universe
  const account = useAtomValue(accountAtom)

  useEffect(() => {
    document.title = `Fork | ${u.appName}`
    history.replaceState(null, '', '/fork')
  }, [u.appName])

  // Nsite-specific fields.
  const [nsiteId, setNsiteId] = useState('hallway')
  const [customTitle, setCustomTitle] = useState<string | null>(null)
  const [nsiteDescription, setNsiteDescription] = useState('')
  const [forkFromNsite, setForkFromNsite] = useState(!!window.hallway.nsite)
  const [nsiteToClone, setNsiteToClone] = useState(() => window.hallway.nsite ?? '')
  const [sourceURL, setSourceURL] = useState(() => window.location.origin)

  // Form state mirrors window.hallway.universe.
  const [state, setState] = useState(() => makeInitialState(u))

  // Title for the nsite manifest / <title> tag: follows the chosen App Name
  // unless the user typed a custom one.
  const nsiteTitle = customTitle ?? state.appName ?? ''

  // Top-level list fields as newline-separated text, parsed on deploy.
  const [lists, setLists] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {}
    for (const f of LIST_FIELDS) init[f] = listToString(u[f] as string[] | undefined)
    return init
  })

  const [invalid, setInvalid] = useState<Record<string, string[]>>({})
  function reportInvalid(key: string, entries: string[]) {
    setInvalid((prev) => {
      const next = { ...prev }
      if (entries.length) next[key] = entries
      else delete next[key]
      return next
    })
  }

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setState((s) => ({ ...s, [key]: value }))
  }

  function setListField(key: string, value: string) {
    setLists((l) => ({ ...l, [key]: value }))
  }

  // Functional updater for a repeatable list in the form state, so async
  // updates (e.g. after NIP-05 resolution) never clobber newer edits.
  function updateItems<K extends RepeatableKey>(key: K) {
    return (fn: (items: FormState[K]) => FormState[K]) =>
      setState((s) => ({ ...s, [key]: fn(s[key]) }))
  }

  // Stages of the deploy pipeline. We keep the latest stage for rendering,
  // and accumulate a simple log for the user.
  const [stage, setStage] = useState<NsiteDeployStage>({ stage: 'idle' })
  const [log, setLog] = useState<string[]>([])
  const [result, setResult] = useState<ReturnType<typeof gatewayUrlsFor> | null>(null)
  const [naddr, setNaddr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  function stageToText(s: NsiteDeployStage): string {
    switch (s.stage) {
      case 'idle':
        return 'idle'
      case 'fetching':
        return `fetching ${s.url}`
      case 'injecting':
        return 'injecting universe JSON into index.html'
      case 'uploading':
        return `uploading ${s.file} → ${s.server}`
      case 'mirroring':
        return `mirroring ${s.file} → ${s.server}`
      case 'signing':
        return 'signing nsite event'
      case 'publishing':
        return `publishing to ${s.relay} (${s.done}/${s.total})`
      case 'done':
        return `done — event ${s.eventId.slice(0, 8)}…`
      case 'error':
        return `error: ${s.error}`
    }
  }

  function isStageRunning(s: NsiteDeployStage): boolean {
    return s.stage !== 'idle' && s.stage !== 'done' && s.stage !== 'error'
  }

  async function deploy() {
    if (!account) {
      toast.error('log in first — nsite publishing needs a signing key')
      return
    }
    if (!isValidDTag(nsiteId)) {
      toast.error(
        'nsite id must be 1-13 chars of lowercase letters, digits or "-", and not end with "-"'
      )
      return
    }

    setBusy(true)
    setResult(null)
    setNaddr(null)
    setLog([])

    try {
      // The form is disabled while busy, so writing the normalized values back
      // can't overwrite anything typed in the meantime.
      const normalized = await normalizeForm(state, lists)
      setState(normalized.state)
      setLists(normalized.lists)
      setInvalid(normalized.invalid)
      if (normalized.errors.length) {
        setStage({ stage: 'idle' })
        toast.error(`Fix the invalid entries first:\n${normalized.errors.join('\n')}`)
        return
      }

      setStage({
        stage: 'fetching',
        url: forkFromNsite ? nsiteToClone : `${sourceURL.replace(/\/+$/, '')}/assets.json`
      })

      const params: DeployNsiteParams = {
        d: nsiteId,
        description: nsiteDescription,
        title: nsiteTitle,
        sourceURL: forkFromNsite ? undefined : sourceURL,
        forkFromNsite: forkFromNsite ? nsiteToClone : undefined,
        universe: compactForUniverse(normalized.state, normalized.lists),
        extraBlossomServers: stringToList(normalized.lists.defaultBlossomServers)
      }

      const res = await deployNsite(params, (s) => {
        setStage(s)
        setLog((l) => [...l, stageToText(s)])
      })
      setNaddr(res.naddr)
      setResult(gatewayUrlsFor(account.pubkey, nsiteId))
    } catch (e) {
      const msg = getErrorMessage(e)
      setStage({ stage: 'error', error: msg })
      setLog((l) => [...l, `error: ${msg}`])
      toast.error(msg)
    } finally {
      setBusy(false)
    }
  }

  return (
    <SecondaryPageLayout ref={ref} index={index} title="Fork">
      <ScrollArea className="h-[calc(100vh-3rem)]">
        <ValidationContext.Provider value={{ invalid, report: reportInvalid }}>
          <div className="px-4 py-4 max-w-3xl mx-auto space-y-6 pb-20">
            {/* Header */}
            <header className="space-y-2">
              <h1 className="text-2xl font-bold">Fork</h1>
              <p className="text-sm text-muted-foreground">
                Publish this instance as a <strong>NIP-5A nsite</strong>: the current universe
                config gets serialized into the deployed
                <code className="mx-1 text-xs bg-muted px-1 rounded">index.html</code>
                and a kind-35128 manifest event is signed and broadcast to your outbox relays, the
                big relays, and the 44billion relay set.
              </p>
            </header>

            <fieldset disabled={busy} className="min-w-0 space-y-6">
              {/* Section: Nsite identification */}
              <Section
                title="Nsite"
                description="Site identifier used as the nsite `d` tag and as the subdomain on nsite gateways."
              >
                <Field
                  label="Nsite ID"
                  hint="1-13 chars: lowercase letters, digits, '-'. Cannot end with '-'."
                >
                  <Input
                    value={nsiteId}
                    onChange={(e) => setNsiteId(e.target.value)}
                    placeholder="myhallway"
                  />
                </Field>
                <Field label="Title" hint="Human-readable title (becomes the `title` tag).">
                  <Input
                    value={nsiteTitle}
                    onChange={(e) => setCustomTitle(e.target.value)}
                    placeholder={u.appName}
                  />
                </Field>
                <Field
                  label="Description"
                  hint="Short description (becomes the `description` tag)."
                >
                  <Textarea
                    rows={3}
                    value={nsiteDescription}
                    onChange={(e) => setNsiteDescription(e.target.value)}
                    placeholder="My Hallway instance."
                  />
                </Field>
                {window.hallway.nsite && (
                  <Field label="Fork from">
                    <RadioGroup
                      className="flex gap-4"
                      value={forkFromNsite ? 'nsite' : 'website'}
                      onValueChange={(value) => setForkFromNsite(value === 'nsite')}
                    >
                      <label className="flex items-center gap-2 text-sm">
                        <RadioGroupItem value="nsite" />
                        nsite
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <RadioGroupItem value="website" />
                        normal website
                      </label>
                    </RadioGroup>
                  </Field>
                )}
                {forkFromNsite && (
                  <Field label="Nsite address" hint="NIP-19 naddr of nsite to clone.">
                    <Input value={nsiteToClone} onChange={(e) => setNsiteToClone(e.target.value)} />
                  </Field>
                )}
                {!forkFromNsite && (
                  <Field
                    label="Source URL"
                    hint="Base URL containing /assets.json and all deployed files."
                  >
                    <Input
                      value={sourceURL}
                      onChange={(e) => setSourceURL(e.target.value)}
                      placeholder={window.location.origin}
                    />
                  </Field>
                )}
              </Section>

              {/* Section: Branding */}
              <Section
                title="Branding"
                description="App identity baked into the deployed index.html."
              >
                <Field label="App Name">
                  <Input
                    value={state.appName ?? ''}
                    onChange={(e) => setField('appName', e.target.value)}
                  />
                </Field>
                <Field label="Logo URL" hint="Leave empty to use the stylized App Name.">
                  <Input
                    value={state.logoUrl ?? ''}
                    onChange={(e) => setField('logoUrl', e.target.value)}
                  />
                </Field>
                <Field label="Base Share URL">
                  <Input
                    value={state.baseShareURL ?? ''}
                    onChange={(e) => setField('baseShareURL', e.target.value)}
                  />
                </Field>
                <Field label="Home Markdown">
                  <Textarea
                    rows={6}
                    value={state.homeMarkdown ?? ''}
                    onChange={(e) => setField('homeMarkdown', e.target.value)}
                  />
                </Field>
              </Section>

              {/* Section: Appearance defaults */}
              <Section
                title="User Defaults"
                description="Initial values for the toggle/preference UI shown to new users."
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Default Theme">
                    <Select
                      value={state.defaultThemeSetting ?? 'system'}
                      onValueChange={(v) =>
                        setField('defaultThemeSetting', v as FormState['defaultThemeSetting'])
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {THEME_KEYS.map((k) => (
                          <SelectItem key={k} value={k}>
                            {k}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Default Primary Color">
                    <Select
                      value={state.defaultPrimaryColor ?? 'TEAL'}
                      onValueChange={(v) =>
                        setField('defaultPrimaryColor', v as FormState['defaultPrimaryColor'])
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PRIMARY_COLOR_KEYS.map((k) => (
                          <SelectItem key={k} value={k}>
                            {k}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Notification List Style">
                    <Select
                      value={state.notificationListStyleDefault || 'unset'}
                      onValueChange={(v) =>
                        setField(
                          'notificationListStyleDefault',
                          v === 'unset'
                            ? undefined
                            : (v as FormState['notificationListStyleDefault'])
                        )
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(['unset', 'compact', 'detailed'] as const).map((k) => (
                          <SelectItem key={k} value={k}>
                            {k}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Normal Feed Style">
                    <Select
                      value={state.noteListStyleDefault || 'unset'}
                      onValueChange={(v) =>
                        setField(
                          'noteListStyleDefault',
                          v === 'unset' ? undefined : (v as FormState['noteListStyleDefault'])
                        )
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(['unset', 'full', 'detailed', 'compact'] as const).map((k) => (
                          <SelectItem key={k} value={k}>
                            {k}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Default Avatar Style">
                    <Select
                      value={state.avatarStyleDefault ?? 'square'}
                      onValueChange={(v) =>
                        setField('avatarStyleDefault', v as FormState['avatarStyleDefault'])
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {AVATAR_KEYS.map((k) => (
                          <SelectItem key={k} value={k}>
                            {k}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                  {BOOL_FIELDS.filter(
                    // these two are rendered next to their relay lists below
                    (f) => f !== 'readRepliesFromExclusive' && f !== 'publishTargetsExclusive'
                  ).map((f) => (
                    <BoolField
                      key={f}
                      label={fieldLabel(f)}
                      checked={!!state[f]}
                      onChange={(v) => setState((s) => ({ ...s, [f]: v }))}
                    />
                  ))}
                </div>
              </Section>

              {/* Section: Feeds */}
              <Section
                title="Feeds"
                description="Fixed feeds displayed for all users on the sidebar/bottom bar."
              >
                <RepeatableList
                  title="Fixed Feeds"
                  itemName="Feed"
                  emptyText="No fixed feeds configured. Add one to pin feeds to the sidebar."
                  items={state.hardcodedFeeds}
                  onChange={updateItems('hardcodedFeeds')}
                  makeItem={() => ({ name: '', icon: '', relays: '', pubkeys: '', kinds: '' })}
                >
                  {(feed, update) => (
                    <>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Field label="Name">
                          <Input
                            value={feed.name}
                            onChange={(e) => update({ name: e.target.value })}
                            placeholder="Local News"
                          />
                        </Field>
                        <Field label="Icon URL">
                          <Input
                            value={feed.icon}
                            onChange={(e) => update({ icon: e.target.value })}
                            placeholder="https://icons.com/news.png"
                          />
                        </Field>
                      </div>
                      <Field label="Relay URLs" hint="One relay URL per line.">
                        <ListTextarea
                          fieldKey={`${feed._id}:relays`}
                          normalize={normalizeRelays}
                          rows={2}
                          value={feed.relays}
                          onChange={(relays) => update({ relays })}
                          placeholder="wss://relay.example.com"
                        />
                      </Field>
                      <Field label="Pubkeys" hint="npub, nprofile, hex or NIP-05, one per line.">
                        <ListTextarea
                          fieldKey={`${feed._id}:pubkeys`}
                          normalize={normalizePubkeys}
                          rows={2}
                          value={feed.pubkeys}
                          onChange={(pubkeys) => update({ pubkeys })}
                          placeholder="npub1..."
                        />
                      </Field>
                      <Field
                        label="Kinds"
                        hint="Restrict this feed to specific event kinds, one number per line (e.g. 30311 for livestreams). Leave empty for all default feed kinds."
                      >
                        <ListTextarea
                          fieldKey={`${feed._id}:kinds`}
                          normalize={normalizeKinds}
                          rows={2}
                          value={feed.kinds}
                          onChange={(kinds) => update({ kinds })}
                          placeholder="30311"
                        />
                      </Field>
                    </>
                  )}
                </RepeatableList>
                <RepeatableList
                  title="Custom Links"
                  itemName="Link"
                  emptyText="No custom links configured. Add one to show external links on the sidebar below the fixed feeds."
                  items={state.customLinks}
                  onChange={updateItems('customLinks')}
                  makeItem={() => ({ label: '', hint: '', url: '' })}
                >
                  {(link, update) => (
                    <>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Field label="Label">
                          <Input
                            value={link.label}
                            onChange={(e) => update({ label: e.target.value })}
                            placeholder="Community Wiki"
                          />
                        </Field>
                        <Field label="Hint" hint="Tooltip shown on hover (HTML title attribute).">
                          <Input
                            value={link.hint}
                            onChange={(e) => update({ hint: e.target.value })}
                            placeholder="Our external wiki"
                          />
                        </Field>
                      </div>
                      <Field label="URL">
                        <Input
                          value={link.url}
                          onChange={(e) => update({ url: e.target.value })}
                          placeholder="https://example.com"
                        />
                      </Field>
                    </>
                  )}
                </RepeatableList>
                <RepeatableList
                  title="Kind Groups"
                  itemName="Group"
                  emptyText="No kind groups configured. Add one to bundle kinds into sidebar filter toggles."
                  items={state.kindGroups}
                  onChange={updateItems('kindGroups')}
                  makeItem={() => ({ kinds: '', label: '', disabled: false })}
                >
                  {(group, update) => (
                    <>
                      <Field
                        label="Label"
                        hint="Falls back to the first kind's registry description."
                      >
                        <Input
                          value={group.label}
                          onChange={(e) => update({ label: e.target.value })}
                          placeholder="Livestreams"
                        />
                      </Field>
                      <Field label="Kinds" hint="One event kind number per line.">
                        <ListTextarea
                          fieldKey={`${group._id}:kinds`}
                          normalize={normalizeKinds}
                          rows={2}
                          value={group.kinds}
                          onChange={(kinds) => update({ kinds })}
                          placeholder="30311"
                        />
                      </Field>
                      <BoolField
                        label="Disabled by default"
                        checked={group.disabled}
                        onChange={(disabled) => update({ disabled })}
                      />
                    </>
                  )}
                </RepeatableList>
                <Field
                  label="Feed Kinds"
                  hint="Explicit list of kinds synced from the user's follow list, one per line. Leave empty to use all kinds from the kind groups."
                >
                  <ListTextarea
                    fieldKey="feedKinds"
                    normalize={normalizeKinds}
                    rows={3}
                    value={state.feedKinds}
                    onChange={(feedKinds) => setField('feedKinds', feedKinds)}
                  />
                </Field>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Grouped Timeframe (seconds)">
                    <Input
                      type="number"
                      value={state.groupedTimeframe || ''}
                      onChange={(e) =>
                        setField('groupedTimeframe', parseInt(e.target.value, 10) || 0)
                      }
                    />
                  </Field>
                  <Field label="Max Embed Depth">
                    <Input
                      type="number"
                      value={state.maxEmbedDepth || ''}
                      onChange={(e) => setField('maxEmbedDepth', parseInt(e.target.value, 10) || 0)}
                    />
                  </Field>
                </div>
              </Section>

              {/* Section: Relays */}
              <Section
                title="Relays"
                description="Relays and servers this instance defaults to for various tasks. One URL per line."
              >
                {RELAYS_SECTION_FIELDS.map((f) => (
                  <Field key={f} label={fieldLabel(f)}>
                    <ListTextarea
                      fieldKey={f}
                      normalize={RELAY_LIST_FIELDS.includes(f) ? normalizeRelays : undefined}
                      rows={3}
                      value={lists[f] ?? ''}
                      onChange={(v) => setListField(f, v)}
                    />
                  </Field>
                ))}
              </Section>

              {/* Section: Reply Sources */}
              <Section title="Reply Sources">
                <Field
                  label="Read Replies From"
                  hint="Extra relay URLs to read replies from; appended to user's read relays when non-exclusive."
                >
                  <ListTextarea
                    fieldKey="readRepliesFrom"
                    normalize={normalizeRelays}
                    rows={3}
                    value={lists.readRepliesFrom}
                    onChange={(v) => setListField('readRepliesFrom', v)}
                  />
                </Field>
                <BoolField
                  label="Read Replies From Exclusive"
                  checked={!!state.readRepliesFromExclusive}
                  onChange={(v) => setField('readRepliesFromExclusive', v)}
                />
              </Section>

              {/* Section: Publish Targets */}
              <Section title="Publish Targets">
                <Field
                  label="Publish Targets"
                  hint="Extra relay URLs that new posts are published to by default."
                >
                  <ListTextarea
                    fieldKey="publishTargets"
                    normalize={normalizeRelays}
                    rows={3}
                    value={lists.publishTargets}
                    onChange={(v) => setListField('publishTargets', v)}
                  />
                </Field>
                <BoolField
                  label="Publish Targets Exclusive"
                  checked={!!state.publishTargetsExclusive}
                  onChange={(v) => setField('publishTargetsExclusive', v)}
                />
              </Section>

              {/* Section: Client Handlers */}
              <Section
                title="Client Handlers"
                description="External apps offered for opening events. URL pattern placeholders: {} (nip19 code), {e}/{id} (hex id), {kind}, {hint}, {p}/{pubkey}, {npub}, {d}."
              >
                <RepeatableList
                  title="By Kind"
                  itemName="Handler"
                  emptyText="No per-kind handlers configured."
                  items={state.clientHandlersByKind}
                  onChange={updateItems('clientHandlersByKind')}
                  makeItem={() => ({ kind: '', name: '', urlPattern: '' })}
                >
                  {(h, update) => (
                    <div className="grid grid-cols-1 sm:grid-cols-[6rem_1fr_2fr] gap-3">
                      <Field label="Kind">
                        <Input
                          inputMode="numeric"
                          value={h.kind}
                          onChange={(e) => update({ kind: e.target.value })}
                          placeholder="30023"
                        />
                      </Field>
                      <Field label="Name">
                        <Input
                          value={h.name}
                          onChange={(e) => update({ name: e.target.value })}
                          placeholder="Habla"
                        />
                      </Field>
                      <Field label="URL Pattern">
                        <Input
                          value={h.urlPattern}
                          onChange={(e) => update({ urlPattern: e.target.value })}
                          placeholder="https://habla.news/a/{}"
                        />
                      </Field>
                    </div>
                  )}
                </RepeatableList>
                <RepeatableList
                  title="Fallback"
                  itemName="Handler"
                  emptyText="No fallback handlers configured."
                  items={state.clientHandlersFallback}
                  onChange={updateItems('clientHandlersFallback')}
                  makeItem={() => ({ name: '', urlPattern: '' })}
                >
                  {(h, update) => (
                    <div className="grid grid-cols-1 sm:grid-cols-[1fr_2fr] gap-3">
                      <Field label="Name">
                        <Input
                          value={h.name}
                          onChange={(e) => update({ name: e.target.value })}
                          placeholder="njump"
                        />
                      </Field>
                      <Field label="URL Pattern">
                        <Input
                          value={h.urlPattern}
                          onChange={(e) => update({ urlPattern: e.target.value })}
                          placeholder="https://njump.me/{}"
                        />
                      </Field>
                    </div>
                  )}
                </RepeatableList>
              </Section>

              {/* Section: Translations */}
              <Section
                title="Translations"
                description="Inline overrides (English key → translated string). They take precedence over the Translations URL."
              >
                <Field label="Translations URL" hint="JSON file with translation overrides.">
                  <Input
                    value={state.translationsURL ?? ''}
                    onChange={(e) => setField('translationsURL', e.target.value)}
                  />
                </Field>
                <RepeatableList
                  title="Overrides"
                  itemName="Override"
                  emptyText="No inline translation overrides."
                  items={state.translations}
                  onChange={updateItems('translations')}
                  makeItem={() => ({ key: '', value: '' })}
                >
                  {(t, update) => (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <Field label="Key">
                        <Input
                          value={t.key}
                          onChange={(e) => update({ key: e.target.value })}
                          placeholder="Notifications"
                        />
                      </Field>
                      <Field label="Value">
                        <Input
                          value={t.value}
                          onChange={(e) => update({ value: e.target.value })}
                          placeholder="Alerts"
                        />
                      </Field>
                    </div>
                  )}
                </RepeatableList>
              </Section>

              {/* Section: Services & Proxies */}
              <Section title="Services & Proxies">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Imgproxy URL">
                    <Input
                      value={state.imgproxyUrl ?? ''}
                      onChange={(e) => setField('imgproxyUrl', e.target.value)}
                    />
                  </Field>
                  <Field label="Link Preview Service">
                    <Input
                      value={state.linkPreviewService ?? ''}
                      onChange={(e) => setField('linkPreviewService', e.target.value)}
                    />
                  </Field>
                  <Field label="Imgproxy Key" hint="HMAC key (hex). Requires the salt too.">
                    <Input
                      value={state.imgproxyKey ?? ''}
                      onChange={(e) => setField('imgproxyKey', e.target.value)}
                    />
                  </Field>
                  <Field label="Imgproxy Salt" hint="HMAC salt (hex). Requires the key too.">
                    <Input
                      value={state.imgproxySalt ?? ''}
                      onChange={(e) => setField('imgproxySalt', e.target.value)}
                    />
                  </Field>
                  <Field label="Generic CORS Proxy">
                    <Input
                      value={state.genericCorsProxy ?? ''}
                      onChange={(e) => setField('genericCorsProxy', e.target.value)}
                    />
                  </Field>
                  <Field label="Registry of Kinds URL">
                    <Input
                      value={state.registryOfKindsURL ?? ''}
                      onChange={(e) => setField('registryOfKindsURL', e.target.value)}
                    />
                  </Field>
                  <Field label="Pomegranate Central">
                    <Input
                      value={state.pomegranateCentral ?? ''}
                      onChange={(e) => setField('pomegranateCentral', e.target.value)}
                    />
                  </Field>
                </div>
                <Field label="Pomegranate Operators">
                  <ListTextarea
                    fieldKey="pomegranateOperators"
                    rows={3}
                    value={lists.pomegranateOperators}
                    onChange={(v) => setListField('pomegranateOperators', v)}
                  />
                </Field>
              </Section>
            </fieldset>

            {/* Deploy button + status */}
            <div className="sticky bottom-0 bg-background/80 backdrop-blur -mx-4 px-4 py-3 border-t border-border flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <Button onClick={deploy} disabled={busy || !account} className="flex-1">
                  {busy ? (
                    <>
                      <HugeiconsIcon icon={Loading03Icon} className="animate-spin size-4" />
                      deploying…
                    </>
                  ) : (
                    <>
                      <HugeiconsIcon icon={SourceCodeIcon} className="size-4" />
                      deploy as nsite
                    </>
                  )}
                </Button>
              </div>

              {!account && (
                <p className="text-xs text-destructive">
                  You need to log in (any signer except{' '}
                  <code className="bg-muted px-1 rounded">npub</code>) to deploy.
                </p>
              )}

              {stage.stage !== 'idle' && (
                <div className="border rounded-lg p-3 bg-card space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    {isStageRunning(stage) ? (
                      <HugeiconsIcon icon={Loading03Icon} className="animate-spin size-4" />
                    ) : stage.stage === 'done' ? (
                      <HugeiconsIcon
                        icon={CheckmarkCircle02Icon}
                        className="size-4 text-green-500"
                      />
                    ) : stage.stage === 'error' ? (
                      <HugeiconsIcon icon={AlertCircleIcon} className="size-4 text-destructive" />
                    ) : null}
                    <code className="text-xs">{stageToText(stage)}</code>
                  </div>
                  {log.length > 0 && (
                    <pre className="text-[10px] leading-snug text-muted-foreground max-h-32 overflow-auto whitespace-pre-wrap break-all">
                      {log.join('\n')}
                    </pre>
                  )}
                </div>
              )}

              {result && (
                <div className="border rounded-lg p-3 bg-card space-y-2">
                  <p className="text-sm font-medium">Published. Open your nsite at:</p>
                  <ul className="space-y-1">
                    {result.map((g) => (
                      <li key={g.url} className="flex items-center gap-2 text-sm">
                        <HugeiconsIcon
                          icon={ArrowRight01Icon}
                          className="size-4 text-muted-foreground"
                        />
                        <a
                          className="text-blue-600 hover:underline break-all"
                          target="_blank"
                          rel="noreferrer"
                          href={g.url}
                        >
                          {g.label} — {g.url}
                        </a>
                      </li>
                    ))}
                  </ul>
                  {naddr && (
                    <p className="text-sm">
                      Also at{' '}
                      <a
                        className="text-blue-600 hover:underline break-all"
                        target="_blank"
                        rel="noreferrer"
                        href={`https://44billion.net/${naddr}`}
                      >
                        https://44billion.net/{naddr}
                      </a>
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </ValidationContext.Provider>
      </ScrollArea>
    </SecondaryPageLayout>
  )
})

ForkPage.displayName = 'ForkPage'
export default ForkPage

// --- Small UI helpers ---------------------------------------------------------

function Section({
  title,
  description,
  children
}: {
  title: string
  description?: string
  children: React.ReactNode
}) {
  return (
    <fieldset className="border border-border bg-neutral-50/40 dark:bg-neutral-900/40 rounded-xl p-4 space-y-3">
      <legend className="text-sm font-semibold px-2">{title}</legend>
      {description && <p className="text-xs text-muted-foreground -mt-2">{description}</p>}
      <div className="space-y-3">{children}</div>
    </fieldset>
  )
}

function Field({
  label,
  hint,
  children
}: {
  label: string
  hint?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      {children}
      {hint && <p className="text-[10px] leading-relaxed text-muted-foreground">{hint}</p>}
    </div>
  )
}

function BoolField({
  label,
  checked,
  onChange
}: {
  label: string
  checked: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <Checkbox checked={checked} onCheckedChange={(v) => onChange(!!v)} />
      <span className="text-xs">{label}</span>
    </label>
  )
}

// A list of editable items, each in its own card with a delete button.
// `onChange` takes an updater so that edits always apply to the latest list.
function RepeatableList<T extends { _id: number }>({
  title,
  itemName,
  emptyText,
  items,
  onChange,
  makeItem,
  children
}: {
  title: string
  itemName: string
  emptyText: string
  items: T[]
  onChange: (fn: (items: T[]) => T[]) => void
  makeItem: () => Omit<T, '_id'>
  children: (item: T, update: (patch: Partial<Omit<T, '_id'>>) => void) => React.ReactNode
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground">{title}</span>
        <Button
          variant="outline"
          size="sm"
          type="button"
          onClick={() => onChange((xs) => [...xs, { ...makeItem(), _id: newItemId() } as T])}
        >
          Add {itemName.toLowerCase()}
        </Button>
      </div>
      {items.length === 0 && <p className="text-xs text-muted-foreground">{emptyText}</p>}
      {items.map((item, i) => (
        <div key={item._id} className="border border-input rounded-lg p-3 space-y-3 bg-background">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-muted-foreground">
              {itemName} {i + 1}
            </span>
            <Button
              variant="ghost"
              size="sm"
              type="button"
              onClick={() => onChange((xs) => xs.filter((x) => x._id !== item._id))}
              className="h-auto p-0 text-xs text-muted-foreground hover:text-destructive"
            >
              Delete
            </Button>
          </div>
          {children(item, (patch) =>
            onChange((xs) => xs.map((x) => (x._id === item._id ? { ...x, ...patch } : x)))
          )}
        </div>
      ))}
    </div>
  )
}

// Newline-separated list input. With `normalize`, entries are normalized on
// blur (e.g. npub → hex, "relay.com" → "wss://relay.com/") and the ones that
// can't be are listed under the field.
function ListTextarea({
  fieldKey,
  normalize,
  value,
  onChange,
  rows,
  placeholder
}: {
  fieldKey: string
  normalize?: Normalizer
  value: string
  onChange: (value: string) => void
  rows?: number
  placeholder?: string
}) {
  const { invalid, report } = useContext(ValidationContext)
  const [checking, setChecking] = useState(false)
  const latest = useRef(value)
  latest.current = value

  async function onBlur() {
    if (!normalize) return
    const text = value
    setChecking(true)
    try {
      const res = await normalize(text)
      // skip if the user kept typing while we were resolving
      if (latest.current !== text) return
      report(fieldKey, res.invalid)
      if (res.text !== text) onChange(res.text)
    } finally {
      setChecking(false)
    }
  }

  const bad = invalid[fieldKey]
  return (
    <>
      <Textarea
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        aria-invalid={!!bad?.length}
      />
      {checking && <p className="text-[10px] text-muted-foreground">checking…</p>}
      {!!bad?.length && (
        <p className="text-[10px] text-destructive break-all">Invalid: {bad.join(', ')}</p>
      )}
    </>
  )
}
